import { Component, ReactNode } from 'react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import * as Sentry from '@sentry/react';
import { logger } from '@/lib/logger';
import { ERROR_MESSAGES, SUPPORT_INFO } from '@/constants/messages';

interface SoulBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  /** Callback chamado antes de resetar o estado - útil para refetch de queries */
  onReset?: () => void;
}

interface SoulBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * 🎨 SoulBoundary - Error Boundary com Alma
 * 
 * Captura erros de forma humana e acolhedora, alinhado à essência Auli.
 * Em vez de telas técnicas de erro, oferece mensagens afetivas e ações claras.
 * 
 * Uso básico:
 * ```tsx
 * <SoulBoundary>
 *   <MinhaFeature />
 * </SoulBoundary>
 * ```
 * 
 * Com recuperação de queries (use SoulBoundaryWithQuery):
 * ```tsx
 * <SoulBoundaryWithQuery>
 *   <FeatureComQueries />
 * </SoulBoundaryWithQuery>
 * ```
 */
export class SoulBoundary extends Component<SoulBoundaryProps, SoulBoundaryState> {
  constructor(props: SoulBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): SoulBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Reporta para Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
    
    logger.error('[SoulBoundary] Erro capturado:', error, errorInfo);
  }

  handleReset = () => {
    // Chama onReset primeiro para refetch de queries (se configurado)
    this.props.onReset?.();
    // Depois limpa o estado de erro
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { fallbackTitle, fallbackMessage } = this.props;
      const messages = ERROR_MESSAGES.boundary;
      
      return (
        <div className="min-h-[50vh] flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center space-y-6 bg-gradient-to-br from-background to-muted border-2 border-border/50">
            {/* Ícone com cor de alerta afetiva */}
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-soul-warmth/10 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-soul-alert" />
              </div>
            </div>
            
            {/* Título Acolhedor */}
            <div className="space-y-2">
              <h2 className="text-2xl font-heading font-bold text-foreground">
                {fallbackTitle || messages.title}
              </h2>
              <p className="text-muted-foreground font-lexend leading-relaxed">
                {fallbackMessage || messages.description}
              </p>
            </div>

            {/* Informação técnica (apenas em DEV) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {messages.technicalDetails}
                </summary>
                <pre className="mt-2 p-3 bg-muted rounded-lg text-xs overflow-auto max-h-32 text-left">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            
            {/* Ações com linguagem humana */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                onClick={this.handleReset}
                className="flex-1 bg-soul-focus hover:bg-soul-achievement text-white"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {messages.retryButton}
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="flex-1"
              >
                <Home className="mr-2 h-4 w-4" />
                {messages.homeButton}
              </Button>
            </div>
            
            {/* Mensagem de suporte */}
            <p className="text-xs text-muted-foreground">
              Se o problema persistir, fale com a gente:{' '}
              <a href={SUPPORT_INFO.mailto} className="underline hover:text-foreground transition-colors">
                {SUPPORT_INFO.email}
              </a>
            </p>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
